import clsx from 'clsx';
import type { PipelineStatus } from '@arcanum-hq/shared-types';

/**
 * Status colour map — superset of two distinct vocabularies:
 *
 * 1. Pipeline statuses (PipelineStatus, 6 values) — keyed to the new
 *    `status-*` CSS-var tokens registered in index.css @theme.
 *    Maps PIPELINE_STATUS_COLOR_TOKEN exactly (conversation-model-versioning
 *    Phase 5 Session 7, 2026-05-17).
 *
 * 2. Share-lifecycle statuses (external-sharing Phase 3, 2026-05-09) —
 *    `Active`, `Awaiting signup`, `Revoked`. MUST PRESERVE — these keys are
 *    consumed by SentShareRow / ReceivedShareRow and break the shares UI if
 *    removed (risk #45 / divergence #4, research/phase-5.md §4/§8).
 *
 * NOTE: `Priced` (4-value legacy DealStatus key) is intentionally ABSENT —
 * it was a stale UI key, not a DB column. Replaced by `Closed` in the
 * 6-value PipelineStatus set.
 */
const STATUS_COLORS: Record<string, string> = {
  // ---- Pipeline statuses (6-value PipelineStatus set) ----
  Analyzing:
    'text-status-analyzing bg-status-analyzing/7 border-status-analyzing/13',
  'In Market':
    'text-status-in-market bg-status-in-market/7 border-status-in-market/13',
  'LOI Submitted':
    'text-status-loi-submitted bg-status-loi-submitted/7 border-status-loi-submitted/13',
  'Under Contract':
    'text-status-under-contract bg-status-under-contract/7 border-status-under-contract/13',
  Closed: 'text-status-closed bg-status-closed/7 border-status-closed/13',
  Passed: 'text-status-passed bg-status-passed/7 border-status-passed/13',

  // ---- Share-lifecycle statuses (external-sharing Phase 3) ----
  // PRESERVE: Active / Awaiting signup / Revoked are live keys consumed by
  // SentShareRow / ReceivedShareRow. Removing them breaks the shares UI.
  // "Awaiting signup" replaced the previous "Pending" label (2026-05-10):
  // pending shares are visible to anonymous viewers via the public deal link
  // — only the recipient's authed inbox + fork capability is gated. "Pending"
  // implied no visibility at all, which was misleading.
  Active: 'text-accent bg-accent/7 border-accent/13',
  'Awaiting signup': 'text-warn bg-warn/7 border-warn/13',
  Revoked: 'text-text-dim bg-input-bg border-panel-border',
};

/**
 * Props accept a wide string union so both pipeline-status callers
 * (PipelineStatus) and share-lifecycle callers (string literals from
 * ShareStatus map) can pass their value without a cast.
 *
 * DO NOT narrow to `PipelineStatus` alone — that breaks share-path callers.
 */
interface StatusBadgeProps {
  status: PipelineStatus | 'Active' | 'Awaiting signup' | 'Revoked' | string;
  /** Optional hover tooltip — useful for ambiguous labels like "Awaiting signup". */
  title?: string;
  className?: string;
}

export function StatusBadge({ status, title, className }: StatusBadgeProps) {
  return (
    <span
      title={title}
      className={clsx(
        'inline-block px-2.5 py-1 rounded-[2px] text-xs font-mono font-semibold tracking-[0.4px] whitespace-nowrap border',
        STATUS_COLORS[status] ||
          'text-text-dim bg-input-bg border-panel-border',
        className
      )}
    >
      {status}
    </span>
  );
}
